import { useMemo, useRef } from 'react';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import { ONBOARDING_PAGE_SIZE } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { UiEntitiesFilter } from '@/modules/onboarding/common/hooks/use-entities-query-filters';
import {
  EntitiesFilter,
  GetEntitiesParams,
} from '@/modules/onboarding/common/types/get-entities.params';

const MAX_SINGLE_FETCH_PAGE_SIZE = 500;

interface UsePaginatedEntitiesParams<TEntity> {
  queryKey: string;
  uiEntitiesFilter: UiEntitiesFilter | null;
  searchString: string | undefined;
  selectedRows?: TEntity[];
  getEntities: (params: GetEntitiesParams) => Promise<PaginatedResponse<TEntity>>;
}

interface UsePaginatedEntitiesReturn<TEntity> {
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  /**
   * Refetch all currently-loaded entities in a single request using a computed page size.
   * Use after mutations (delete, merge) to avoid cursor-shift duplicates from multi-page
   * infinite query refetches. If the computed size exceeds MAX_SINGLE_FETCH_PAGE_SIZE,
   * falls back to a normal page-1 refetch (pageSize = ONBOARDING_PAGE_SIZE).
   *
   * @param removedCount - Net change in entity count after the mutation. For delete this
   *   matches removedIds.length, but for merge it is smaller because multiple stale rows
   *   are stripped while only N - 1 entities actually disappear from the dataset.
   * @param removedIds - Entity IDs to strip from the cached first page so the table
   *   shows correct data immediately while the background refetch is in-flight.
   */
  refetchAfterMutation: (
    currentlyLoadedCount: number,
    removedCount: number,
    removedIds: number[]
  ) => Promise<TEntity[] | undefined>;
  refetchAfterAddition: (
    currentlyLoadedCount: number,
    addedCount: number
  ) => Promise<TEntity[] | undefined>;
  hasNextPage: boolean;
  entities: TEntity[];
  total: number;
}

export function usePaginatedEntities<TEntity>({
  uiEntitiesFilter,
  searchString,
  getEntities,
  selectedRows = [],
  queryKey,
}: UsePaginatedEntitiesParams<TEntity>): UsePaginatedEntitiesReturn<TEntity> {
  // Holds a one-shot page size override for the next first-page fetch.
  // Set before resetting the query so the queryFn picks it up, then cleared immediately.
  const overridePageSizeRef = useRef<number | null>(null);
  const queryClient = useQueryClient();

  const {
    data: rawData,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: [queryKey, uiEntitiesFilter, searchString],
    staleTime: 1 * 60 * 1000,
    queryFn: async ({ pageParam }) => {
      // Only make API call if filter is not 'selected' (handle selected locally)
      if (uiEntitiesFilter === UiEntitiesFilter.SELECTED) {
        return new PaginatedResponse({
          items: [],
          pagination: {
            cursor: null,
            total: 0,
          },
        });
      }

      let entitiesFilter: EntitiesFilter | undefined;
      switch (uiEntitiesFilter) {
        case UiEntitiesFilter.DUPLICATES:
          entitiesFilter = EntitiesFilter.DUPLICATES;
          break;
        case UiEntitiesFilter.ERRORS:
          entitiesFilter = EntitiesFilter.ERRORS;
          break;
        case UiEntitiesFilter.WARNINGS:
          entitiesFilter = EntitiesFilter.WARNINGS;
          break;
        default:
          entitiesFilter = undefined;
      }

      // On first-page fetches (pageParam === undefined), use the override size if set.
      const isFirstPage = pageParam === undefined;
      const pageSize =
        isFirstPage && overridePageSizeRef.current !== null
          ? overridePageSizeRef.current
          : ONBOARDING_PAGE_SIZE;

      if (isFirstPage) {
        overridePageSizeRef.current = null;
      }

      return await getEntities({
        filter: entitiesFilter,
        cursor: pageParam,
        searchString,
        pageSize,
      });
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (_lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, page) => sum + page.items.length, 0);
      const total = allPages[allPages.length - 1]?.pagination?.total ?? 0;
      if (totalLoaded >= total) return undefined;
      return allPages[allPages.length - 1]?.pagination?.cursor ?? undefined;
    },
  });

  const refetchAfterMutation = async (
    currentlyLoadedCount: number,
    removedCount: number,
    removedIds: number[]
  ) => {
    const needed = currentlyLoadedCount - removedCount;

    // Round up to the nearest multiple of ONBOARDING_PAGE_SIZE so we always fetch
    // in clean multiples (e.g. 85 remaining → pageSize 100 with ONBOARDING_PAGE_SIZE=25).
    const rounded =
      Math.ceil(Math.max(needed, ONBOARDING_PAGE_SIZE) / ONBOARDING_PAGE_SIZE) *
      ONBOARDING_PAGE_SIZE;

    // If the user scrolled so far that a single fetch would be too large, fall back
    // to a normal first page to avoid hammering the backend.
    const pageSize = rounded > MAX_SINGLE_FETCH_PAGE_SIZE ? ONBOARDING_PAGE_SIZE : rounded;

    overridePageSizeRef.current = pageSize;

    // Always invalidate the unfiltered "All" query — delete/merge mutations affect
    // the full list regardless of which filter tab is active when they happen.
    // When on the SELECTED tab, uiEntitiesFilter is 'selected' but the All-tab cache
    // key uses null, so we must target null explicitly to avoid stale rows persisting.
    const allTabKey = [queryKey, null, searchString];

    // Trim the cache to a single page AND strip stale entities so the table shows
    // correct data immediately while the background refetch is in-flight.
    // removedIds can be larger than removedCount for merges: both source rows must
    // disappear from the optimistic cache, but the dataset only shrinks by N - 1.
    const removedIdSet = new Set(removedIds);
    queryClient.setQueryData(allTabKey, (old: any) => {
      if (!old) return old;
      const firstPage = old.pages[0];
      if (!firstPage) return old;

      const filteredItems =
        removedIdSet.size > 0
          ? firstPage.items.filter((item: any) => !removedIdSet.has(item.id))
          : firstPage.items;

      return {
        ...old,
        pages: [
          {
            ...firstPage,
            items: filteredItems,
            pagination: {
              ...firstPage.pagination,
              total: Math.max(0, (firstPage.pagination?.total ?? 0) - removedCount),
            },
          },
        ],
        pageParams: [old.pageParams[0]],
      };
    });
    // Invalidate all filtered-tab caches, not only the active one.
    // Merge/delete mutations can happen while "Selected" is active, but the user may
    // immediately switch back to Duplicates/Errors/Warnings. Those caches must refetch.
    [UiEntitiesFilter.DUPLICATES, UiEntitiesFilter.ERRORS, UiEntitiesFilter.WARNINGS].forEach(
      filter => {
        void queryClient.invalidateQueries({
          queryKey: [queryKey, filter, searchString],
          exact: true,
        });
      }
    );

    try {
      const freshPage = await getEntities({
        filter: undefined,
        cursor: undefined,
        searchString,
        pageSize,
      });

      queryClient.setQueryData(allTabKey, {
        pages: [freshPage],
        pageParams: [undefined],
      });

      return freshPage.items;
    } catch {
      // Fall back to the normal query invalidation path if the explicit refresh fails.
      void queryClient.invalidateQueries({
        queryKey: allTabKey,
        exact: true,
        refetchType: 'all',
      });

      return undefined;
    }
  };

  const refetchAfterAddition = async (currentlyLoadedCount: number, addedCount: number) => {
    const needed = currentlyLoadedCount + addedCount;

    const rounded =
      Math.ceil(Math.max(needed, ONBOARDING_PAGE_SIZE) / ONBOARDING_PAGE_SIZE) *
      ONBOARDING_PAGE_SIZE;

    const pageSize = rounded > MAX_SINGLE_FETCH_PAGE_SIZE ? ONBOARDING_PAGE_SIZE : rounded;

    overridePageSizeRef.current = pageSize;

    const allTabKey = [queryKey, null, searchString];

    try {
      const freshPage = await getEntities({
        filter: undefined,
        cursor: undefined,
        searchString,
        pageSize,
      });

      queryClient.setQueryData(allTabKey, {
        pages: [freshPage],
        pageParams: [undefined],
      });

      return freshPage.items;
    } catch {
      void queryClient.invalidateQueries({
        queryKey: allTabKey,
        exact: true,
        refetchType: 'all',
      });

      return undefined;
    }
  };

  const allItems = useMemo(() => rawData?.pages.flatMap(page => page.items) ?? [], [rawData]);
  const lastPagination = rawData?.pages[rawData.pages.length - 1]?.pagination;
  const isSelectedFilter = uiEntitiesFilter === UiEntitiesFilter.SELECTED;
  const hasNextPage = isSelectedFilter ? false : !!lastPagination?.cursor;
  const entities = isSelectedFilter ? selectedRows : allItems;
  const total = isSelectedFilter ? selectedRows.length : (lastPagination?.total ?? 0);

  return {
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
    refetchAfterMutation,
    refetchAfterAddition,
    hasNextPage,
    entities,
    total,
  };
}
